import { useCallback } from 'react';

import type { Updater } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from 'api/constants';
import { resolve } from 'api/resolve';
import { useUpdateByQueryKey } from 'hooks/useUpdateByQueryKey';
import type { Template } from 'state/selectedTemplate/selectedTemplateStore';
import type { Nullable } from 'utils/values';
import { assertDefined, hasValue } from 'utils/values';

import { useTemplatesApi } from '../templates-api';
import { sortNodesInTemplate } from '../utils/sortNodesInTemplate';

const getQueryKey = (templateId: Nullable<string>) => (hasValue(templateId) ? [QUERY_KEYS.TEMPLATES, templateId] : []);

/** Fetch template by template ID */
export function useFetchTemplate<T = Template>(templateId: Nullable<string>, select?: (data: Template) => T) {
  const { getById } = useTemplatesApi();

  return useQuery({
    queryKey: getQueryKey(templateId),
    queryFn: async () => {
      const response = await resolve(getById(assertDefined(templateId, 'Template ID is required')));

      // An unknown id comes back as a success with no payload, which used to reach sortNodesInTemplate
      // and throw "Cannot read properties of undefined (reading 'nodes')". React Query reports that as
      // the query's error, so the UI showed an internal TypeError instead of saying what was wrong.
      if (!hasValue(response)) {
        throw new Error('This blueprint could not be found. It may have been deleted.');
      }

      return sortNodesInTemplate(response);
    },
    enabled: hasValue(templateId),
    select,
    retry: false // This is to prevent long spinner when template is deleted.
  });
}

/** Update Template cache without refetch. */
export function useUpdateTemplate() {
  const update = useUpdateByQueryKey();

  return useCallback(
    (templateId: string, updater: Updater<Template, Nullable<Template>>) => {
      const queryKey = getQueryKey(templateId);
      return update(queryKey, updater);
    },
    [update]
  );
}
